import moment from 'moment'
import SmsCounter from './SmsCounter'
import {toNumber, isNaN, round, toUpper} from 'lodash'

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      // Resolve with the base64-encoded string when the file is successfully read
      if (reader.result && typeof reader.result === 'string') {
        resolve(reader.result) // Extract the base64 part
      } else {
        reject(new Error('Invalid result type or missing result'))
      }
    }

    reader.onerror = (error) => {
      // Reject with the error if there's an issue reading the file
      reject(error)
    }

    // Read the file as a data URL
    reader.readAsDataURL(file)
  })
}

export const base64ToFile = (base64String: string): File => {
  // Extract metadata from the base64 string
  const [, mimeType, encoding, data] = /^data:(.+);(\S+),(.+)$/.exec(base64String) || []

  if (!mimeType || !encoding || !data) {
    throw new Error('Invalid base64 string')
  }

  const byteCharacters = atob(data)
  const byteArrays: Uint8Array[] = []

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, {type: mimeType})

  // Extract the file name from the base64 string
  const [, fileName] = /^data:(.+);.+$/i.exec(base64String) || []

  if (!fileName) {
    throw new Error('Unable to extract file name from base64 string')
  }

  // Create a File object from the Blob
  return new File([blob], fileName, {type: mimeType})
}

export const capitalizeFirstLetter = (str: string): string => {
  // Check if the input is a valid string
  if (typeof str !== 'string' || str.length === 0) {
    return str
  }

  // Capitalize the first letter and concatenate the rest of the string
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const findMatchingArrayName = (inputString: string, arrayObjects: any): string => {
  // Iterate through each array object in the 'arrayObjects' parameter
  for (const arrayObject of arrayObjects) {
    // Check if the inputString is present in the current array
    if (arrayObject.array.some((item: any) => item?.value === inputString)) {
      // If there's a match, return the name of the current array object
      return arrayObject?.name ?? ''
    }
  }
  // If no match is found, return null or handle it as needed
  return '#SharedNum#'
}

export const checkAndFormatDate = (scheduledAt: string | undefined): string => {
  // Convert the scheduledAt to UTC
  const scheduledAtUTC = moment?.utc(scheduledAt)

  // Get the current UTC time
  const currentUTC = moment?.utc()

  // Calculate the difference in minutes
  const timeDifference = scheduledAtUTC?.diff(currentUTC, 'minutes')

  // Check if the difference is less than 5 minutes
  if (timeDifference < 5) {
    const scheduledAtLocal = scheduledAtUTC.local()
    // Return the formatted date (adjust the format as needed)
    return 'Send Now'
  } else {
    const scheduledAtLocal = scheduledAtUTC.local()
    // Return the formatted date (adjust the format as needed)
    // console.log(timeDifference, 'scheduledAtLocalscheduledAtLocalscheduledAtLocal', scheduledAtUTC)
    return moment(scheduledAtLocal)?.format('MMMM Do, yyyy h:mm a')
  }
}

export const ensureHttpProtocol = (url: string): string => {
  // Check if the URL starts with "http://" or "https://"
  if (!url?.startsWith('http://') && !url?.startsWith('https://')) {
    // If not, add "http://"
    return 'http://' + url
  }

  // If the URL already has a valid protocol, return it as is
  return url
}

export const processDataOld = (
  inputString: string,
  extraCount: number = 0,
  isMultiply: boolean = false
): {credit: number; length: number; maxdata: number} => {
  const maxDataArray: number[] = [160, 145, 153, 153, 153, 153, 153, 154]
  const maxLength: number = 1224

  const inputLength: number = inputString?.length + extraCount

  let credit: number = 0
  let alphaCredit: number = 0
  let remainingLength: number = inputLength
  let maxData: number = 0

  for (let i = 0; i < maxDataArray.length; i++) {
    const segmentLength: number = Math.min(maxDataArray[i], remainingLength)
    remainingLength -= segmentLength

    if (remainingLength <= 0) {
      credit = i + 1
      if (isMultiply) {
        alphaCredit = Number((credit * 1.3).toFixed(2))
      }
      maxData = maxDataArray[i]
      break
    }
  }

  const totalAccumulatedLength: number = maxDataArray
    .slice(0, credit - 1)
    .reduce((sum, value) => sum + value, 0)
  const length: number = inputLength - totalAccumulatedLength

  return {
    credit: isMultiply ? alphaCredit : credit,
    length,
    maxdata: maxData,
  }
}

export const processData = (
  inputString: string,
  extraCount: number = 0,
  isMultiply: boolean = true
): {credit: number; length: number; maxdata: number; type: string, perMessageLengh: number} => {
  let extraWord = "-".repeat(extraCount)
  const count = SmsCounter.count(inputString+extraWord)

  return {
    credit: isMultiply ? count.messages * 1.3 : count.messages,
    length: count.remaining,
    maxdata: count?.per_message,
    perMessageLengh: count.length,
    type: count?.encoding.toLowerCase().includes('gsm') ? 'GSM' : 'Unicode',
  }
}

export const createFirstLetter = (inputString: string): {firstName: string; lastName: string} => {
  const words = inputString?.split(' ')
  const firstName = words?.[0]?.charAt(0)
  const lastName = words?.length > 1 ? words?.[1]?.charAt(0) : ''

  return {
    firstName,
    lastName,
  }
}

export const findBigDate = (date1: string | null, date2: string | null): string | null => {
  // Get the current time
  const currentTime = new Date()

  // Check if either date is null
  if (date1 === null && date2 === null) {
    return null // Both dates are null
  } else if (date1 === null) {
    return date2 // Date1 is null, so return date2
  } else if (date2 === null) {
    return date1 // Date2 is null, so return date1
  }

  const parsedDate1 = new Date(date1)
  const parsedDate2 = new Date(date2)

  const diff1 = Math.abs(parsedDate1.getTime() - currentTime.getTime())
  const diff2 = Math.abs(parsedDate2.getTime() - currentTime.getTime())

  // Compare differences and return the date with the smaller difference
  if (diff1 < diff2) {
    return date1
  } else {
    return date2
  }
}

export const isStaff = (role: string, assign: boolean = false): boolean => {
  if (assign) return role == 'sales' || role == 'accountManager'
  return true
}

export const isAdmin = (role: string): boolean => {
  return role == 'superAdmin' || role == 'subAdmin'
}

export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined || value === '') {
    return false
  }

  if (Array.isArray(value) && value.length === 0) {
    return false
  }

  if (value instanceof File) {
    return true
  }

  if (typeof value === 'object') {
    if (Object.keys(value).length === 0) {
      return false
    }
  }

  return true
}

function removeCodeAfterDash(link: string): string {
  return link?.replace(/-.*?(?=\s|$)/g, '')
}

export const processLinks = (inputString: string): string => {
  // Split the string into lines and process each line
  return inputString
    ?.split('\n')
    ?.map((line) => {
      if (line?.includes('gosite.au')) {
        return line?.replace(/gosite\.au.*?(?=\s|$)/g, (match) => removeCodeAfterDash(match))
      } else {
        return line
      }
    })
    .join('\n')
}

export const replaceMessageWith = (text: string, replacedText: string) => {
  return (
    text
      ?.replaceAll("\n\nReply 'STOP' to opt-out", '')
      .replaceAll("\n\nReply 'STOP' to opt-out", '')
      .replaceAll("\nReply 'STOP' to opt-out", '')
      .replaceAll("Reply 'STOP' to opt-out", '')
      .replaceAll("\n\nReply 'STOP' to opt-ou", '')
      .replaceAll("\nReply 'STOP' to opt-ou", '')
      .replaceAll("Reply 'STOP' to opt-ou", '')
      .replaceAll('\n\nReply STOP to opt-out', '')
      .replaceAll('\n\nReply STOP to opt-out', '')
      .replaceAll('\nReply STOP to opt-out', '')
      .replaceAll('Reply STOP to opt-out', '')
      .replaceAll('\n\nReply STOP to opt-ou', '')
      .replaceAll('\nReply STOP to opt-ou', '')
      .replaceAll('Reply STOP to opt-ou', '')
      .replaceAll('\n\nSTOP: stoptxt.au/vc', '')
      .replaceAll('\nSTOP: stoptxt.au/vc', '')
      .replaceAll('STOP: stoptxt.au/vc', '')
      .replaceAll('\n\nSTOP: stoptxt.au/v', '')
      .replaceAll('\nSTOP: stoptxt.au/v', '')
      .replaceAll('STOP: stoptxt.au/v', '')

      .replaceAll('\n\nSTOP: dev.stoptxt.au/vc', '')
      .replaceAll('\nSTOP: dev.stoptxt.au/vc', '')
      .replaceAll('STOP: dev.stoptxt.au/vc', '')
      .replaceAll('\n\nSTOP: dev.stoptxt.au/v', '')
      .replaceAll('\nSTOP: dev.stoptxt.au/v', '')
      .replaceAll('STOP: dev.stoptxt.au/v', '')

      .replaceAll('\n\nSTOP: m.stoptxt.au/vc', '')
      .replaceAll('\nSTOP: m.stoptxt.au/vc', '')
      .replaceAll('STOP: m.stoptxt.au/vc', '')
      .replaceAll('\n\nSTOP: m.stoptxt.au/v', '')
      .replaceAll('\nSTOP: m.stoptxt.au/v', '')
      .replaceAll('STOP: m.stoptxt.au/v', '')

      .replaceAll('\n\nSTOP: stoptxt.dev.sev-2.com/vc', '')
      .replaceAll('\nSTOP: stoptxt.dev.sev-2.com/vc', '')
      .replaceAll('STOP: stoptxt.dev.sev-2.com/vc', '')
      .replaceAll('\n\nSTOP: stoptxt.dev.sev-2.com/v', '')
      .replaceAll('\nSTOP: stoptxt.dev.sev-2.com/v', '')
      .replaceAll('STOP: stoptxt.dev.sev-2.com/v', '') + replacedText
  )
}

export const removeOptOutOrReply = (message: string) => {
  let replacedMessage = message

  if (replacedMessage?.includes("Reply 'STOP' to opt-ou")) {
    return replaceMessageWith(replacedMessage, '')
  }

  if (replacedMessage?.includes('STOP: stoptxt.au/v')) {
    return replaceMessageWith(replacedMessage, '')
  }

  return replacedMessage
}

const convertNumberToStringAndRemoveSpaces = (number: string) => {
  return number.toString().replace(/-|\s/g, '')
}

const cehckMobileNumberValidation = (number: string) => {
  var regex =
    /^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[4])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/gm
  const reg = new RegExp(regex)
  return number.match(reg) ? number : ''
}

const checkIsLessThan9Digit = (number: string) => {
  return number.length < 9 ? '' : number
}

const removeAnyNonDigitCharacter = (number: string) => {
  return number.replace(/\D/g, '')
}

const removeCountryCode = (result: string) => {
  const str = result.toString()

  if (str.length === 12) {
    return str.substring(3)
  } else if (str.length === 11) {
    return str.substring(2)
  } else if (str.length === 10) {
    return str.substring(1)
  }

  return str
}

const validateDuplicateInList = (number: string, contacts: any) => {
  return contacts.get(number)
}

export const validatePhoneNumberWithMessage = (
  number: any,
  contacts: Map<any, any>,
  validateDuplicate = true
) => {
  if (!number)
    return {
      plainNumber: number,
      formattedNumber: '',
      error: 'Number empty',
      isValid: false,
    }

  let result = convertNumberToStringAndRemoveSpaces(number)

  result = removeAnyNonDigitCharacter(result)
  result = checkIsLessThan9Digit(result)
  if (!result) {
    return {
      plainNumber: number,
      formattedNumber: result,
      error: 'Number is less than 9 digit',
      isValid: false,
    }
  }
  result = cehckMobileNumberValidation(result)
  if (!result) {
    return {
      plainNumber: number,
      formattedNumber: result,
      error: 'Number is not valid',
      isValid: false,
    }
  }

  result = removeCountryCode(result)

  if (validateDuplicate && validateDuplicate === true) {
    const isDuplicate = validateDuplicateInList(result, contacts)
    if (isDuplicate) {
      return {
        plainNumber: number,
        formattedNumber: result,
        error: 'Number is duplicate',
        isValid: false,
      }
    }
    contacts.set(result, number)
  }

  return {
    plainNumber: number,
    formattedNumber: result,
    error: '',
    isValid: true,
  }
}

export const validateArrayPhones = (phones: any[]) => {
  const contacts = new Map()

  return phones
    .map((it) => {
      return validatePhoneNumberWithMessage(it, contacts, true)
    })
    .filter((it) => it.isValid)
    .map((it) => it.formattedNumber)
}

export const contactRegionRegex: any = {
  AU: /^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[4])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/gm,
  US: /^(\+?1\s?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/gm,
  UK: /^(\+?44\s?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/gm,
  NZ: /^(\+?64\s?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/gm,
  ID: /^(\+?62\s?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/gm,
}

export const validatePhoneNumberRegion = (phoneNumber: string, region: string) => {
  const regex = contactRegionRegex[region.toUpperCase()]

  if (!regex) {
    throw new Error(`Unsupported region: ${region}`)
  }
  const result = regex.test(phoneNumber)

  return result
}

export const validateArrayPhonesRegion = (number: any[], region: string) => {
  const contacts = new Map()

  return number
    .map((it) => {
      return validatePhoneNumberRegionWithMessage({
        number: it,
        region,
        contacts,
        validateDuplicate: true,
      })
    })
    .filter((it) => it.isValid)
    .map((it) => it?.cleanNumber)
}

export const constantsRegion: any = {
  CountryCodeMap: {
    AU: '+61',
    US: '+1',
    NZ: '+64', // Add more country codes as needed
    UK: '+44',
    ID: '+62',
  },
  AuRegion: 'AU',
  UsRegion: 'US',
  NzRegion: 'NZ',
  UkRegion: 'UK',
  IdRegion: 'ID',
}

type PhoneNumberValidationResult = {
  cleanNumber: string
  fullNumber: string
  originalNumber: string
  errorMessage: string
  isValid: boolean
}

function preprocessPhoneNumber(number: string): string {
  // Remove all non-numeric characters (spaces, hyphens, dots, parentheses)
  return number.replace(/[\s\-\.\(\)]/g, '')
}

function removeCountryCodeRegion(
  region: string,
  number: string
): {cleanNumber: string; countryCode: string} {
  const countryCode = constantsRegion.CountryCodeMap[region.toUpperCase()]
  if (!countryCode) return {cleanNumber: number, countryCode: ''}

  if (number.startsWith(countryCode)) {
    return {cleanNumber: number.slice(countryCode.length), countryCode}
  } else if (number.startsWith(countryCode.replace('+', ''))) {
    return {cleanNumber: number.slice(countryCode.length - 1), countryCode}
  }

  return {cleanNumber: number, countryCode}
}

function validateAustralianPhoneNumber(number: string): void {
  // Australian phone number regex
  const re = contactRegionRegex['AU']
  if (!number.match(re)?.length) {
    throw new Error('Invalid format for Australian phone number')
  }

  if (number.length !== 9) {
    throw new Error('Invalid length for Australian phone number')
  }
}

function validateUSPhoneNumber(number: string): void {
  // US phone number regex (10 digits, no country code)
  const re = contactRegionRegex['US']

  if (!number.match(re)?.length) {
    throw new Error('Invalid format for US phone number')
  }

  if (number.length < 10) {
    throw new Error('Invalid length for US phone number')
  }
}

function validateNZPhoneNumber(number: string) {
  const re = contactRegionRegex.NZ

  if (!number.match(re)?.length) {
    throw new Error('Invalid format for NZ phone number')
  }

  if (number.length < 10) {
    throw new Error('Invalid length for NZ phone number')
  }
}

function validateUKPhoneNumber(number: string) {
  const re = contactRegionRegex.UK

  if (!number.match(re)?.length) {
    throw new Error('Invalid format for UK phone number')
  }

  if (number.length < 10) {
    throw new Error('Invalid length for UK phone number')
  }
}

function validateIDPhoneNumber(number: string) {
  const re = contactRegionRegex.ID

  if (!number.match(re)?.length) {
    throw new Error('Invalid format for ID phone number')
  }

  if (number.length < 10) {
    throw new Error('Invalid length for ID phone number')
  }
}

export function validatePhoneNumberRegionWithMessage({
  number,
  region,
  validateDuplicate,
  contacts,
}: {
  number: string
  region: string
  validateDuplicate?: boolean
  contacts: Map<any, any>
}): PhoneNumberValidationResult {
  const result: PhoneNumberValidationResult = {
    originalNumber: number,
    fullNumber: '',
    cleanNumber: '',
    errorMessage: '',
    isValid: false,
  }

  const upperedRegion = toUpper(region)

  if (!number) {
    result.errorMessage = 'Number is empty'
    return result
  }

  let cleanNumber = preprocessPhoneNumber(number)

  // Remove leading zero if present
  cleanNumber = cleanNumber.startsWith('0') ? cleanNumber.slice(1) : cleanNumber

  // Remove country code
  const {cleanNumber: processedNumber, countryCode} = removeCountryCodeRegion(
    upperedRegion,
    cleanNumber
  )
  cleanNumber = processedNumber

  result.fullNumber = `${countryCode}${cleanNumber}`
  result.cleanNumber = cleanNumber
  try {
    switch (upperedRegion) {
      case constantsRegion.AuRegion:
        validateAustralianPhoneNumber(cleanNumber)
        break
      case constantsRegion.UsRegion:
        validateUSPhoneNumber(cleanNumber)
        break
      case constantsRegion.NzRegion:
        validateNZPhoneNumber(cleanNumber)
        break
      case constantsRegion.UkRegion:
        validateUKPhoneNumber(cleanNumber)
        break
      case constantsRegion.IdRegion:
        validateIDPhoneNumber(cleanNumber)
        break
      default:
        throw new Error('Unsupported Region')
    }

    result.isValid = true
  } catch (error) {
    result.errorMessage = (error as Error).message
    result.isValid = false
  }

  if (validateDuplicate && validateDuplicate === true) {
    const isDuplicate = validateDuplicateInList(result.cleanNumber, contacts)
    if (isDuplicate) {
      return {
        ...result,
        isValid: false,
        errorMessage: 'Number is duplicate',
      }
    }
    contacts.set(result.cleanNumber, number)
  }
  return result
}

export const addTax = (price: any) => {
  const currentPrice = toNumber(price)
  // 10% tax GST
  const taxPrice = currentPrice * 0.1
  const finalPrice = currentPrice + taxPrice

  return isNaN(finalPrice) ? '' : round(finalPrice)
}

export const addQueryParams = (query: any) => {
  const queryParams = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (!value) continue
    if (key.includes('[')) {
      // Convert keys like "searchFieldsNames[0]" to "searchFieldsNames[]"
      const normalizedKey = key.replace(/\[\d+\]/, '[]')
      queryParams.append(normalizedKey, value as string)
    } else {
      queryParams.append(key, value as string)
    }
  }

  return queryParams
}


const DEFAULT_REPLY_STOP_OUT = `Reply 'Stop' to opt out  `
export const DEFAULT_REPLY_STOP_OUT_COUNT = DEFAULT_REPLY_STOP_OUT.length
